import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { FLOOR_PLANS } from "@/lib/constants";
import { ASSETS } from "@/lib/constants";

export const FloorPlans = () => {
  const [index, setIndex] = useState(-1);
  const open = index >= 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + FLOOR_PLANS.length) % FLOOR_PLANS.length);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % FLOOR_PLANS.length);
      if (e.key === "Escape") setIndex(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const current = open ? FLOOR_PLANS[index] : null;

  return (
    <section id="floor-plans" className="relative py-24 md:py-32 border-t border-white/5" data-testid="floor-plans-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-20 max-w-3xl"
        >
          <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-5">Floor Plans</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F8F6F0] leading-[1.05] tracking-tight">
            Every level,{" "}
            <span className="italic bg-gradient-to-r from-[#ffb413] via-[#D4AF37] to-[#ff9900] text-transparent bg-clip-text">
              thoughtfully planned.
            </span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#A0A0A0] font-light">
            Click any plan to view full-size.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ show: { transition: { staggerChildren: 0.12 } } }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {FLOOR_PLANS.map((fp, i) => {
            // Get the image for this floor plan type
            const planImage = ASSETS[fp.imageKey];
            
            return (
              <motion.button
                key={fp.id}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
                }}
                whileHover={{ y: -8 }}
                onClick={() => setIndex(i)}
                data-testid={`floorplan-card-${fp.id}`}
                className="group text-left bg-[#0A0A0B] border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-500 overflow-hidden"
              >
                <div className="aspect-[4/3] relative overflow-hidden">
                  {planImage ? (
                    <img 
                      src={planImage} 
                      alt={fp.alt || fp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-80" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                  <Layers className="absolute top-5 left-5 w-5 h-5 text-[#D4AF37]/70" />
                  <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                    <div className="font-serif italic text-5xl text-[#D4AF37]/80 leading-none">{fp.n}</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0] group-hover:text-[#D4AF37] transition-colors">
                      View →
                    </div>
                  </div>
                </div>
                <div className="p-5 border-t border-white/10">
                  <div className="font-serif text-xl text-[#F8F6F0] tracking-tight">{fp.title}</div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setIndex(-1)}>
        <DialogContent
          className="max-w-5xl w-[94vw] bg-[#0A0A0B] border-[#D4AF37]/30 text-[#F8F6F0] p-0 overflow-hidden"
          data-testid="floorplan-dialog"
        >
          {current && (
            <div className="relative">
              <div className="aspect-video relative">
                {(() => {
                  const currentImage = ASSETS[current.imageKey];
                  return currentImage ? (
                    <img 
                      src={currentImage} 
                      alt={current.alt || current.title}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                      <Layers className="w-12 h-12 text-[#D4AF37]/70 mb-5" />
                      <div className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] mb-3">{current.n}</div>
                      <div className="font-serif text-3xl md:text-5xl text-[#F8F6F0] tracking-tight">{current.title}</div>
                      <p className="mt-4 text-xs md:text-sm text-[#A0A0A0] max-w-md font-light">
                        Detailed plan available on request. Contact our team for the full brochure.
                      </p>
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
                <button
                  onClick={() => setIndex((i) => (i - 1 + FLOOR_PLANS.length) % FLOOR_PLANS.length)}
                  className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="floorplan-prev"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <div className="text-xs uppercase tracking-[0.3em] text-[#A0A0A0]">
                  {index + 1} / {FLOOR_PLANS.length}
                </div>
                <button
                  onClick={() => setIndex((i) => (i + 1) % FLOOR_PLANS.length)}
                  className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="floorplan-next"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};